import React, { useState, useRef } from 'react';
import { Upload, AlertCircle, CheckCircle, Package, SkipForward } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const CATEGORIES = ['Electronics', 'Groceries', 'Fashion', 'Health & Beauty', 'Sports', 'Home & Living', 'Accessories', 'Monitors', 'General'];

export default function Step3FirstProduct({ partnerProfile, onComplete, onSkip }) {
  const [form, setForm] = useState({
    product_name: '',
    sku: '',
    category: '',
    price: '',
    stock_status: 'in_stock',
    description: '',
    is_budget_alternative: false,
    alternative_to: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const inputStyle = {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(0,229,255,0.15)',
    fontFamily: 'DM Sans, sans-serif',
  };

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form?.product_name?.trim()) { setError('Product name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      let imageUrl = '';

      if (imageFile) {
        const ext = imageFile?.name?.split('.')?.pop();
        const fileName = `${partnerProfile?.id ?? 'product'}-${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase?.storage
          ?.from('product-images')
          ?.upload(fileName, imageFile, { upsert: true });
        if (uploadErr) { setError('Image upload failed: ' + uploadErr?.message); setSaving(false); return; }
        const { data: urlData } = supabase?.storage?.from('product-images')?.getPublicUrl(fileName);
        imageUrl = urlData?.publicUrl ?? '';
      }

      const { error: insertErr } = await supabase
        ?.from('retail_products')
        ?.insert({
          partner_id: partnerProfile?.id,
          product_name: form?.product_name,
          sku: form?.sku,
          category: form?.category || 'General',
          price: parseFloat(form?.price) || 0,
          stock_status: form?.stock_status,
          description: form?.description,
          image_url: imageUrl,
          product_status: 'active',
        });

      if (insertErr) { setError(insertErr?.message); return; }

      // Update onboarding step
      await supabase?.from('partner_profiles')?.update({ onboarding_step: 4 })?.eq('id', partnerProfile?.id);

      setSuccess(true);
      setTimeout(() => onComplete?.(), 1400);
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>Add your first product to the FinVisAR catalog</h1>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Products in our catalog get discovered by consumers during AR scans. Start with your best seller.</p>
      </div>
      {success ? (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center"
            style={{ background: 'rgba(0,229,255,0.1)', border: '2px solid rgba(0,229,255,0.4)' }}
          >
            <CheckCircle size={36} style={{ color: '#00E5FF' }} />
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Product Added! ✨</p>
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Your product is now live in the FinVisAR catalog.</p>
          </div>
          <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Moving to final step...</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Product image upload */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Product Image</label>
            <div
              className="flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all"
              style={{ border: '2px dashed rgba(0,229,255,0.25)', background: 'rgba(0,229,255,0.02)' }}
              onClick={() => fileInputRef?.current?.click()}
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Product image preview" className="w-16 h-16 object-cover rounded-xl" style={{ border: '1px solid rgba(0,229,255,0.2)' }} />
              ) : (
                <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.3)' }}>
                  <Package size={24} style={{ color: '#475569' }} />
                </div>
              )}
              <div>
                <p className="text-sm font-medium" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{imagePreview ? 'Click to change image' : 'Upload product image'}</p>
                <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>PNG, JPG, WebP up to 5MB</p>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
            </div>
          </div>

          {/* Form fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Product Name <span style={{ color: '#EF4444' }}>*</span></label>
              <input type="text" value={form?.product_name} onChange={e => setForm(f => ({ ...f, product_name: e?.target?.value }))} placeholder="e.g. Sony WH-1000XM5 Headphones"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>SKU / Product Code</label>
              <input type="text" value={form?.sku} onChange={e => setForm(f => ({ ...f, sku: e?.target?.value }))} placeholder="e.g. SNY-WH1000XM5"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Category</label>
              <select value={form?.category} onChange={e => setForm(f => ({ ...f, category: e?.target?.value }))}
                className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: form?.category ? '#E2E8F0' : '#64748B' }}>
                <option value="" style={{ background: '#0D1B2A' }}>Select category</option>
                {CATEGORIES?.map(c => <option key={c} value={c} style={{ background: '#0D1B2A' }}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Price ($)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>$</span>
                <input type="number" min="0" step="0.01" value={form?.price} onChange={e => setForm(f => ({ ...f, price: e?.target?.value }))} placeholder="0.00"
                  className="w-full pl-7 pr-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Stock Status</label>
              <div className="flex gap-2">
                {[{ value: 'in_stock', label: 'In Stock' }, { value: 'low_stock', label: 'Low Stock' }, { value: 'out_of_stock', label: 'Out of Stock' }]?.map(opt => (
                  <label key={opt?.value} className="flex items-center gap-1.5 cursor-pointer">
                    <div
                      onClick={() => setForm(f => ({ ...f, stock_status: opt?.value }))}
                      className="w-4 h-4 rounded-full flex items-center justify-center cursor-pointer"
                      style={{
                        border: form?.stock_status === opt?.value ? '2px solid #00E5FF' : '2px solid rgba(255,255,255,0.2)',
                        background: form?.stock_status === opt?.value ? 'rgba(0,229,255,0.2)' : 'transparent',
                      }}
                    >
                      {form?.stock_status === opt?.value && <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#00E5FF' }} />}
                    </div>
                    <span className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{opt?.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Short Description <span className="text-xs" style={{ color: '#475569' }}>({form?.description?.length ?? 0}/200)</span></label>
              <textarea value={form?.description} onChange={e => setForm(f => ({ ...f, description: e?.target?.value?.slice(0, 200) }))} placeholder="Brief product description..."
                rows={3} className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none resize-none" style={inputStyle} />
            </div>
          </div>

          {/* Budget alternative toggle */}
          <div
            className="p-4 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,229,255,0.1)' }}
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Budget-friendly alternative?</p>
                <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Mark this product as a budget alternative in AR Scanner</p>
              </div>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, is_budget_alternative: !f?.is_budget_alternative }))}
                className="relative w-11 h-6 rounded-full transition-all duration-300"
                style={{ background: form?.is_budget_alternative ? 'linear-gradient(135deg, #00E5FF, #7B2FBE)' : 'rgba(255,255,255,0.1)' }}
              >
                <div
                  className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-300"
                  style={{ left: form?.is_budget_alternative ? '22px' : '2px' }}
                />
              </button>
            </div>
            {form?.is_budget_alternative && (
              <div className="mt-3">
                <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Alternative to which product type?</label>
                <input type="text" value={form?.alternative_to} onChange={e => setForm(f => ({ ...f, alternative_to: e?.target?.value }))} placeholder="e.g. Premium wireless headphones"
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
              <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onSkip}
              className="flex items-center gap-1.5 px-5 py-3 rounded-xl text-sm font-medium"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}
            >
              <SkipForward size={14} /> Skip for now
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
              style={{
                background: saving ? 'rgba(0,229,255,0.3)' : 'linear-gradient(135deg, #00E5FF, #7B2FBE)',
                fontFamily: 'DM Sans, sans-serif',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Adding Product...' : 'Add Product & Continue'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
